import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavigationBar from '../../components/NavigationBar';
import NotificationBanner from '../../components/NotificationBanner';
import Modal from '../../components/Modal';
import DealForm from './components/DealForm';
import SuccessMessage from './components/SuccessMessage';

const DealEditModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showNotification, setShowNotification] = useState(true);

  const mockContacts = [
    {
      id: 1,
      name: "Sarah Johnson",
      email: "sarah.johnson@techcorp.com",
      company: "TechCorp Solutions",
      appointmentDate: "2026-01-25"
    },
    {
      id: 2,
      name: "Michael Chen",
      email: "michael.chen@innovate.io",
      company: "Innovate Digital",
      appointmentDate: "2026-01-28"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      email: "emily.r@globalventures.com",
      company: "Global Ventures Inc",
      appointmentDate: "2026-02-02"
    },
    {
      id: 4,
      name: "David Thompson",
      email: "d.thompson@nexusgroup.com",
      company: "Nexus Group",
      appointmentDate: "2026-02-05"
    },
    {
      id: 5,
      name: "Lisa Anderson",
      email: "lisa.anderson@brightfuture.com",
      company: "Bright Future Enterprises",
      appointmentDate: "2026-02-10"
    }
  ];

  const mockDeal = {
    id: 1,
    clientName: "Sarah Johnson",
    dealValue: 45000,
    expectedCloseDate: "2026-02-15",
    stage: "Proposal",
    createdDate: "2026-01-10"
  };

  useEffect(() => {
    const dismissed = localStorage.getItem('deal-edit-notification-dismissed');
    if (dismissed) {
      setShowNotification(false);
    }
  }, []);

  const handleSave = (updatedDeal) => {
    setIsLoading(true);

    setTimeout(() => {
      console.log('Deal updated:', updatedDeal);
      setIsLoading(false);
      setShowSuccess(true);
    }, 1500);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      navigate('/pipeline-view');
    }, 250);
  };

  const handleSuccessClose = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      navigate('/pipeline-view');
    }, 250);
  };

  const handleNotificationDismiss = () => {
    setShowNotification(false);
    localStorage.setItem('deal-edit-notification-dismissed', 'true');
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      {showNotification && (
        <NotificationBanner
          message="Changes made here will update the deal across Pipeline View and Dashboard statistics"
          type="warning"
          onDismiss={handleNotificationDismiss}
        />
      )}
      <div className={`${showNotification ? 'pt-[120px] md:pt-[130px]' : 'pt-[60px]'} pb-8 px-4 md:px-6 lg:px-8`}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2 md:mb-3">
              Deal Management
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Edit deal information and update pipeline status
            </p>
          </div>

          <div className="bg-card rounded-2xl shadow-elevation-2 p-6 md:p-8 lg:p-10">
            <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-[#8B7EC8] to-[#A8C8EC] flex items-center justify-center">
                <span className="text-xl md:text-2xl font-semibold text-white">
                  {mockDeal?.clientName?.charAt(0)}
                </span>
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                  {mockDeal?.clientName}
                </h2>
                <p className="text-sm md:text-base text-muted-foreground">
                  Current Stage: {mockDeal?.stage}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8 p-4 md:p-6 bg-muted rounded-xl">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
                  Current Deal Value
                </p>
                <p className="text-lg md:text-xl font-semibold text-foreground">
                  ₹{mockDeal?.dealValue?.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
                  Expected Close Date
                </p>
                <p className="text-lg md:text-xl font-semibold text-foreground">
                  {new Date(mockDeal.expectedCloseDate)?.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">
                  Created Date
                </p>
                <p className="text-lg md:text-xl font-semibold text-foreground">
                  {new Date(mockDeal.createdDate)?.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>

            <div className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 p-4 bg-[#F4A6CD]/10 rounded-lg border border-[#F4A6CD]/20">
              <p>
                <strong className="text-foreground">Note:</strong> Click "Save Changes" to update this deal across all views. The changes will be reflected immediately in your Pipeline View and Dashboard statistics.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={handleCancel}
        title={showSuccess ? "Deal Updated" : "Edit Deal Information"}
        footer={null}
      >
        {showSuccess ? (
          <SuccessMessage
            message="Deal information has been successfully updated and synchronized across all views."
            onClose={handleSuccessClose}
          />
        ) : (
          <DealForm
            deal={mockDeal}
            contacts={mockContacts}
            onSave={handleSave}
            onCancel={handleCancel}
            isLoading={isLoading}
          />
        )}
      </Modal>
    </div>
  );
};

export default DealEditModal;